import type {
  IUseInclassMessageBroker,
  TInclassChannelMetadata,
  TInclassMessage,
} from './use-inclass-message-broker-types';

import { useMessageBrokerChannel, type IChannelMessage } from '@cuemath/cue-message-broker';
import { useCallback, useMemo } from 'react';

const CHANNEL_MESSAGES_TTL = 3; // 3 hours

const useInClassMessageBroker: IUseInclassMessageBroker = options => {
  const { studentClassroomId, onMessageReceive, logger } = options;
  const channelId = `inclass_${studentClassroomId}`;
  const channelOptions = useMemo(
    () => ({
      channelId,
      ttl: CHANNEL_MESSAGES_TTL,
      noHistory: true,
      logger,
    }),
    [channelId, logger],
  );

  const handleMessageReceive = useCallback(
    (newMessages: Array<IChannelMessage<TInclassMessage>>, initialData?: boolean) => {
      onMessageReceive?.(
        newMessages.map(channelMessage => channelMessage.message.payload),
        initialData,
      );
    },
    [onMessageReceive],
  );

  const {
    channel,
    publish,
    channelStatus,
    channelMetadata,
    setChannelMetadata,
    removeChannelMetadata,
  } = useMessageBrokerChannel<TInclassMessage, TInclassChannelMetadata>({
    channelOptions,
    consumerFn: onMessageReceive ? handleMessageReceive : undefined,
    metadataRequired: true,
    enabled: studentClassroomId !== undefined,
  });

  const closeChannel = useCallback(() => {
    channel?.destroyChannel();
  }, [channel]);

  return {
    channelStatus,
    channelMetadata,
    setChannelMetadata,
    removeChannelMetadata,
    publish,
    closeChannel,
  };
};

export default useInClassMessageBroker;
